import React from 'react';

import { Pagination, Select, Table, Toolbar } from '../../../design-system/components/desktop';
import {
  ConsoleEmpty,
  ConsoleError,
  StatusPill,
} from '../../../design-system/patterns/console';
import { formatCurrency } from '../../../utils/common';

import '../../Dashboard/dashboard.css';

/**
 * Transaction history, the view. Pure.
 *
 * WHAT DIED HERE
 * --------------
 * - `GenericTable` and its hand-rolled skeleton.
 * - `TableFilters` and the `CalendarDateRangePicker` it mounted, whose 26
 *   theme overrides in `legacy-overrides.css` set `background: #ffffff` on the
 *   popover in light theme and `#1F2937` in dark, with a `rgba(34, 211, 238, ...)`
 *   cyan on every hover. The date range is two plain date fields now, and it
 *   still writes the same `{ start, end }` object the query already read.
 * - A `<Star size={24} color="#1a1a1a">` glyph decorating every card name,
 *   which was neither a rating nor a favourite and rendered black on black in
 *   dark theme. Decorative fill is not allowed by section 8 and it carried no
 *   meaning, so it is gone rather than restyled.
 *
 * Prices are RANDS through `formatCurrency`, which is also what renders "No
 * price" rather than "R0.00" for a missing one.
 */
export function TransectionTableView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,
  status = '',
  onStatusChange,
  statusOptions = [],
  dateRange = { start: '', end: '' },
  onDateRangeChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
}) {
  const filtered = Boolean(search || status || dateRange.start || dateRange.end);

  const columns = [
    { key: 'card', header: 'Card' },
    {
      key: 'price',
      header: 'Price',
      numeric: true,
      render: (row) => formatCurrency(row.price),
    },
    {
      key: 'date',
      header: 'Date',
      muted: true,
      render: (row) => (row.date ? new Date(row.date).toLocaleDateString('en-ZA') : 'Unknown'),
    },
    {
      key: 'counterparty',
      header: 'Counterparty',
      render: (row) => row.counterparty || 'Not recorded',
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
  ];

  return (
    <Table
      columns={columns}
      data={error ? [] : rows}
      rowKey={(row, index) => `${row.date}-${index}`}
      loading={loading}
      emptyState={
        error ? (
          <ConsoleError error={error} onRetry={onRetry} title="Could not load your transactions" />
        ) : (
          <ConsoleEmpty
            filtered={filtered}
            title="No transactions yet"
            text="Every completed sale appears here with what it earned you."
            filteredText="No transaction matches that search, status or date range. Widen one of them and your earnings come back."
            onClear={() => {
              if (onSearchChange) onSearchChange('');
              if (onStatusChange) onStatusChange(null);
              if (onDateRangeChange) onDateRangeChange({ start: '', end: '' });
            }}
          />
        )
      }
      toolbar={
        <Toolbar
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Filter by card name"
          searchLabel="Filter your transactions"
          count={loading ? undefined : `${total} ${total === 1 ? 'transaction' : 'transactions'}`}
          filters={
            <div className="dsa-dash__filters">
              <Select
                options={statusOptions}
                value={status || ''}
                onChange={onStatusChange}
                placeholder="All statuses"
                label="Filter by status"
              />
              {/* Two real date inputs. A month-grid popover for a filter that
                  is applied on change is more chrome than the job needs, and
                  the one this replaced was 161 lines with a hardcoded white
                  background it kept in dark theme. */}
              <label className="dsk-field">
                <span className="dsk-sr-only">Transactions from</span>
                <input
                  type="date"
                  className="dsk-input"
                  value={dateRange.start || ''}
                  max={dateRange.end || undefined}
                  aria-label="Transactions from"
                  onChange={(event) =>
                    onDateRangeChange({ ...dateRange, start: event.target.value })
                  }
                />
              </label>
              <label className="dsk-field">
                <span className="dsk-sr-only">Transactions until</span>
                <input
                  type="date"
                  className="dsk-input"
                  value={dateRange.end || ''}
                  min={dateRange.start || undefined}
                  aria-label="Transactions until"
                  onChange={(event) => onDateRangeChange({ ...dateRange, end: event.target.value })}
                />
              </label>
            </div>
          }
        />
      }
      pagination={
        pageCount > 1 ? (
          <Pagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            totalItems={total}
            itemNoun="transactions"
            onPageChange={onPageChange}
          />
        ) : null
      }
    />
  );
}

export default TransectionTableView;
